import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { NumberParam, StringParam, useQueryParam } from 'use-query-params';
import { CachePolicies, useFetch } from 'use-http';
import { Card } from '@/components/card';
import { usePagination } from '@/webapi/use-pagination';
import { getQueryDate } from '@/features/dashboard/reports/context';
import { DateRange } from '@/features/dashboard/reports/models';
import {
  DateRangeParam,
  EndDateParameter,
  StartDateParameter,
} from '@/features/dashboard/shared/date-picker/date-picker';
import { allMetricDescriptions, MetricKind } from '@/webapi/models';
import { routes } from '@/webapi/routes';
import { VSpace } from '@/components/spacing';
import { Pagination } from '@/components/pagination';
import {
  List,
  SourcesResponse,
  Variant,
} from '@/features/details/utm-source/models';
import { Experience } from '@/webapi/use-experience-api';
import { getUplift } from '@/features/details/summary/util';
import { SummaryResponse, VariantSummary } from '@/webapi/use-analytics-api';
import { MetricValue, MetVal } from '@/features/details/utm-source/metVal';
import { Tooltip } from '@/components/tooltip';
import { currencySymbol } from '@/utils/currencies';
import {
  isSmoothOutlierEnabled,
  SummaryContext,
} from '@/features/details/summary/context';

interface Props {
  experience: Experience;
  apiEndpoint?: string;
}

const getSingleVariant = (
  current: SourcesResponse,
  isMultiVariantControl: (x: List, v: Variant) => boolean,
) =>
  current?.list
    ?.flatMap((x) => x?.variants?.map((v) => isMultiVariantControl(x, v)))
    .reduce((a, b) => a && b, true);

export function SourceReport({ experience, apiEndpoint }: Props): JSX.Element {
  const { post } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
      loading: true,
    },
  }));
  const { showSummary } = useContext(SummaryContext);
  const [dateRange] = useQueryParam<DateRange>(`rn`, DateRangeParam);
  const [startDate] = useQueryParam(`s`, StartDateParameter);
  const [sessType] = useQueryParam(`st`, StringParam);
  const [endDate] = useQueryParam(`e`, EndDateParameter);
  const [version] = useQueryParam(`v`, NumberParam);
  const [audience] = useQueryParam<string>(`aud`, StringParam);
  const smoothOutlierEnabled = isSmoothOutlierEnabled(experience);
  let goal = getSupportedGoal(experience);
  const customMetrics = experience?.elementTracking?.map((x) => ({
    name: x.name,
    ordinal: x.ordinal,
  }));
  const getUtmSource = async (
    page: number,
    size: number,
  ): Promise<SourcesResponse> =>
    post(apiEndpoint || routes.utmSource, {
      experienceId: experience.id,
      audiences: audience ? [audience] : [],
      startAt: getQueryDate(`s`),
      endAt: getQueryDate(`e`),
      version,
      sessionType: sessType || undefined,
      goal,
      page,
      size,
      smoothRevenue: smoothOutlierEnabled,
      customMetrics,
    });
  const pageSize = 4;
  const { total, current, loading, page, maxPages, setPage, refreshList } =
    usePagination(getUtmSource, pageSize, { version });
  const isMultiVariantControl = getMultiVariantControl(current);
  const isSingleVariant = getSingleVariant(current, isMultiVariantControl);
  if (isSingleVariant) {
    goal = MetricKind.ADD_TO_CART_RATE;
  }
  const { odsToWinOrAlt, isExtraGoal } = altOdsToWin(goal);
  useEffect(() => {
    setTimeout(refreshList, 600);
  }, [dateRange, startDate, endDate, version, audience, sessType]);

  const symbol = currencySymbol();
  if (!showSummary || current?.list?.length === 0 || !current?.list) {
    return null;
  }

  return (
    <DetailsCard>
      <Title>Split By Traffic source</Title>
      <Grid margin="1rem 2rem -1rem 2rem">
        <ColHead>Variation</ColHead>
        <ColHead>
          Sessions
          <Tooltip
            text={allMetricDescriptions.get(MetricKind.SESSIONS)}
            title="Sessions"
          />
        </ColHead>
        <ColHead>
          Transactions
          <Tooltip
            text={allMetricDescriptions.get(MetricKind.TRANSACTIONS)}
            title="Transactions"
          />
        </ColHead>
        <ColHead>
          CR
          <Tooltip
            text={allMetricDescriptions.get(MetricKind.CONVERSION_RATE)}
            title="Conversion Rate"
          />
        </ColHead>
        {!apiEndpoint ? (
          <ColHead>
            AOV
            <Tooltip
              text={allMetricDescriptions.get(MetricKind.AVG_ORDER_VALUE)}
              title="Avarage Order Value"
            />
          </ColHead>
        ) : (
          <ColHead>
            ATC%
            <Tooltip
              text={allMetricDescriptions.get(MetricKind.ADD_TO_CART_RATE)}
              title="Add to cart rate"
            />
          </ColHead>
        )}
        <ColHead>
          PSV
          <Tooltip
            text={allMetricDescriptions.get(MetricKind.PER_SESSION_VALUE)}
            title="Per Session Value"
          />
        </ColHead>
        <ColHead>
          Revenue
          <Tooltip
            text={allMetricDescriptions.get(MetricKind.REVENUE)}
            title="Revenue"
          />
        </ColHead>
        <ColHead>
          {odsToWinOrAlt}
          {isExtraGoal ? (
            <Tooltip
              text={allMetricDescriptions.get(goal)}
              title={`${goal?.toLowerCase()?.replace(`_`, ` `)}`}
            />
          ) : (
            <Tooltip
              title="Odds to Win"
              text="The probability Version A is Better than Version B. Based on the primary metric."
            />
          )}
        </ColHead>
      </Grid>
      {current?.list?.map((x, i) => (
        <SourceTableWrapper ke={`${i}`}>
          <SourceName>
            The traffic source is: <strong>{x.utmSource}</strong>
          </SourceName>
          <VSpace />
          {x?.variants
            .sort((x, b) =>
              getVariantName(experience, x.variantId)?.localeCompare(
                getVariantName(experience, b.variantId),
              ),
            )
            ?.map((v) => {
              const {
                sessUplift,
                transUplift,
                CrUplift,
                aovUplift,
                psfUplift,
                revUplift,
                atcUplift,
                clicksUplift,
                ppsUplift,
                signupsUplift,
                subsUplift,
                checkoutUplift,
              } = calcUplifts(current, x, experience, v);
              const {
                sessions,
                transactions,
                cr,
                aov,
                psv,
                rev,
                goalValue,
                showUplift,
              } = getRowValues(v, goal, current, x);
              return (
                <>
                  <VSpace value={2} />
                  <Grid>
                    <MetricValue>
                      {getVariantName(experience, v.variantId)}
                    </MetricValue>
                    <MetVal
                      value={sessions}
                      id={id(MetricKind.SESSIONS, v, x)}
                      uplift={sessUplift}
                      showUplift={false}
                    />
                    <MetVal
                      value={transactions}
                      id={id(MetricKind.TRANSACTIONS, v, x)}
                      uplift={transUplift}
                      showUplift={
                        goal === MetricKind.TRANSACTIONS && showUplift
                      }
                      hideTooltip={goal === MetricKind.TRANSACTIONS}
                    />
                    <MetVal
                      value={cr}
                      id={id(MetricKind.CONVERSION_RATE, v, x)}
                      suffix="%"
                      uplift={CrUplift}
                      showUplift={
                        goal === MetricKind.CONVERSION_RATE && showUplift
                      }
                      hideTooltip={goal === MetricKind.CONVERSION_RATE}
                    />
                    {!apiEndpoint ? (
                      <MetVal
                        value={aov}
                        id={id(MetricKind.AVG_ORDER_VALUE, v, x)}
                        prefix={symbol}
                        uplift={aovUplift}
                        showUplift={
                          goal === MetricKind.AVG_ORDER_VALUE && showUplift
                        }
                        hideTooltip={goal === MetricKind.AVG_ORDER_VALUE}
                      />
                    ) : (
                      <MetVal
                        value={aov}
                        id={id(MetricKind.ADD_TO_CART_RATE, v, x)}
                        suffix="%"
                        uplift={atcUplift}
                        showUplift={
                          goal === MetricKind.ADD_TO_CART_RATE && showUplift
                        }
                        hideTooltip={goal === MetricKind.ADD_TO_CART_RATE}
                      />
                    )}
                    <MetVal
                      value={psv}
                      id={id(MetricKind.PER_SESSION_VALUE, v, x)}
                      prefix={symbol}
                      uplift={psfUplift}
                      showUplift={
                        goal === MetricKind.PER_SESSION_VALUE && showUplift
                      }
                      hideTooltip={goal === MetricKind.PER_SESSION_VALUE}
                    />
                    <MetVal
                      value={rev}
                      id={id(MetricKind.REVENUE, v, x)}
                      prefix={symbol}
                      uplift={revUplift}
                      showUplift={goal === MetricKind.REVENUE && showUplift}
                      hideTooltip={goal === MetricKind.REVENUE}
                    />
                    {isExtraGoal ? (
                      <MetVal
                        value={goalValue}
                        id={`${id(goal, v, x)}extraGoal`}
                        prefix={
                          getExtraGoalPrefix(goal, symbol) === symbol && symbol
                        }
                        suffix={getExtraGoalPrefix(goal, symbol) === `%` && `%`}
                        uplift={getExtraMetric(
                          goal,
                          atcUplift,
                          clicksUplift,
                          ppsUplift,
                          signupsUplift,
                          subsUplift,
                          checkoutUplift,
                        )}
                        showUplift
                        hideTooltip
                      />
                    ) : (
                      <MetricValue>
                        {isMultiVariantControl(x, v)
                          ? `---`
                          : `${v.OdsToWin || 0}%`}
                      </MetricValue>
                    )}
                  </Grid>
                </>
              );
            })}
        </SourceTableWrapper>
      ))}
      <VSpace value={2} />
      <Pagination
        value={page + 1}
        pageCount={maxPages}
        pageSize={pageSize}
        total={total}
        loading={loading}
        onPageChanged={(page) => setPage(page - 1)}
        label="sources"
      />
    </DetailsCard>
  );
}

const getExtraGoalPrefix = (goal: MetricKind, symbol: string): string => {
  if (goal === MetricKind.PROFIT_PER_SESSION) {
    return symbol;
  }
  if (
    goal.toString().toLowerCase().includes(`rate`) ||
    goal === MetricKind.CTR
  ) {
    return `%`;
  }
  return ``;
};
const SourceTableWrapper = styled.div`
  background-color: #fafafa;
  border-radius: 15px;
  padding: 2rem 2rem 3rem 2rem;
  width: 100%;
`;
const SourceName = styled.div`
  text-align: start;
  font-family: Inter, serif;
  color: #a6afb8;
  font-weight: 400;
  font-size: 1.3rem;

  strong {
    font-size: 1.4rem;
  }
`;
const ColHead = styled.div`
  display: flex;
  text-align: start;
  font-family: Inter, serif;
  color: #a6afb8;
  font-weight: 600;
  font-size: 1.3rem;

  .tooltip-question-mark {
    margin: 0;
    margin-left: 1rem;
  }
`;
const Grid = styled.div<{ margin?: string }>`
  display: grid;
  grid-template-columns: 1.8fr repeat(7, 1fr);
  grid-row-gap: 2rem;
  grid-gap: 3rem;
  margin: ${(p) => p.margin || 0};
`;
const Title = styled.div`
  display: flex;
  align-items: center;
  margin: 0;
  font-family: Inter, serif;
  color: black;
  font-weight: 800;
  padding-left: 1.5rem;
  font-size: 1.8rem;
`;
const DetailsCard = styled(Card)`
  && {
    max-width: 1220px;
    cursor: default;
    display: grid;
    grid-row-gap: 2.5rem;
    padding: 3.5rem;
    border-radius: 2rem;
  }
`;

const getVariantName = (experience: Experience, variantId: string) => {
  const name = experience?.variants?.find(
    (variant) => variant.id === variantId,
  )?.name;
  if (name === `A`) {
    return ``;
  }
  return name || ``;
};

const mVal = (v: any, metricName): number =>
  v?.metrics?.find((m) => m.name === metricName)?.value || 0;

const shouldShowUplift = (v: Variant, current: SourcesResponse, x: List) =>
  v.variantId === current.controlVariationId && x.variants.length > 1;

const asSummary = (current: SourcesResponse, x: List, experience: Experience) =>
  ({
    controlVariationId: current.controlVariationId,
    variants: x.variants.map(
      (v) =>
        ({
          metrics: v.metrics,
          variantId: v.variantId,
        } as VariantSummary),
    ),
    experienceId: experience.id,
    assistedProducts: [],
  } as SummaryResponse);
const calcUplifts = (
  current: SourcesResponse,
  x: List,
  experience: Experience,
  v: Variant,
) => {
  const sessUplift = getUplift(
    asSummary(current, x, experience),
    MetricKind.SESSIONS,
    experience.variants,
    v.variantId,
  );
  const transUplift = getUplift(
    asSummary(current, x, experience),
    MetricKind.TRANSACTIONS,
    experience.variants,
    v.variantId,
  );
  const CrUplift = getUplift(
    asSummary(current, x, experience),
    MetricKind.CONVERSION_RATE,
    experience.variants,
    v.variantId,
  );
  const aovUplift = getUplift(
    asSummary(current, x, experience),
    MetricKind.AVG_ORDER_VALUE,
    experience.variants,
    v.variantId,
  );
  const psfUplift = getUplift(
    asSummary(current, x, experience),
    MetricKind.PER_SESSION_VALUE,
    experience.variants,
    v.variantId,
  );
  const revUplift = getUplift(
    asSummary(current, x, experience),
    MetricKind.REVENUE,
    experience.variants,
    v.variantId,
  );
  const ppsUplift = getUplift(
    asSummary(current, x, experience),
    MetricKind.PROFIT_PER_SESSION,
    experience.variants,
    v.variantId,
  );
  const signupsUplift = getUplift(
    asSummary(current, x, experience),
    MetricKind.SIGNUPS_RATE,
    experience.variants,
    v.variantId,
  );
  const atcUplift = getUplift(
    asSummary(current, x, experience),
    MetricKind.ADD_TO_CART_RATE,
    experience.variants,
    v.variantId,
  );

  const checkoutUplift = getUplift(
    asSummary(current, x, experience),
    MetricKind.CHECKOUT_RATE,
    experience.variants,
    v.variantId,
  );
  const clicksUplift = getUplift(
    asSummary(current, x, experience),
    MetricKind.CTR,
    experience.variants,
    v.variantId,
  );

  const subsUplift = getUplift(
    asSummary(current, x, experience),
    MetricKind.SUBSCRIPTION_RATE,
    experience.variants,
    v.variantId,
  );
  return {
    sessUplift,
    transUplift,
    CrUplift,
    aovUplift,
    psfUplift,
    revUplift,
    ppsUplift,
    signupsUplift,
    atcUplift,
    clicksUplift,
    subsUplift,
    checkoutUplift,
  };
};

const id = (mettic: MetricKind, v: Variant, x: List) =>
  `m${mettic}${v.variantId}${x.utmSource}`;

const altOdsToWin = (goal: MetricKind) => {
  switch (goal) {
    case MetricKind.PROFIT_PER_SESSION:
      return { odsToWinOrAlt: `Profit Per Session`, isExtraGoal: true };
    case MetricKind.CTR:
      return { odsToWinOrAlt: `CTR`, isExtraGoal: true };
    case MetricKind.ADD_TO_CART_RATE:
      return { odsToWinOrAlt: `Add To Cart Rate`, isExtraGoal: true };
    case MetricKind.CHECKOUT_RATE:
      return { odsToWinOrAlt: `Checkout Rate`, isExtraGoal: true };
    case MetricKind.SUBSCRIPTION_RATE:
      return { odsToWinOrAlt: `Subscriptions Rate`, isExtraGoal: true };
    case MetricKind.SIGNUPS_RATE:
      return { odsToWinOrAlt: `Signups Rate`, isExtraGoal: true };
    default:
      return { odsToWinOrAlt: `Odds To Win`, isExtraGoal: false };
  }
};

const getMultiVariantControl =
  (current: SourcesResponse) => (x: List, v: Variant) =>
    (x.variants.length > 2 && v.variantId === current.controlVariationId) ||
    x.variants.length === 1;
const getRowValues = (
  v: Variant,
  goal: MetricKind,
  current: SourcesResponse,
  x: List,
) => {
  const sessions = mVal(v, MetricKind.SESSIONS).toLocaleString();
  const transactions = mVal(v, MetricKind.TRANSACTIONS).toLocaleString();
  const cr = parseFloat(
    mVal(v, MetricKind.CONVERSION_RATE).toFixed(2),
  ).toLocaleString();
  const aov = parseFloat(
    mVal(v, MetricKind.AVG_ORDER_VALUE).toFixed(2),
  ).toLocaleString();
  const psv = parseFloat(
    mVal(v, MetricKind.PER_SESSION_VALUE).toFixed(2),
  ).toLocaleString();
  const rev = parseInt(
    mVal(v, MetricKind.REVENUE).toFixed(0),
    10,
  ).toLocaleString();
  const goalValue = parseFloat(mVal(v, goal).toFixed(2)).toLocaleString();
  const showUplift = !shouldShowUplift(v, current, x);
  return { sessions, transactions, cr, aov, psv, rev, goalValue, showUplift };
};

const getSupportedGoal = (experience: Experience): MetricKind => {
  const goal = experience.primaryGoal || experience.goals?.[0];

  if (goal === MetricKind.PROFIT || goal === MetricKind.PROFIT_PER_SESSION) {
    return MetricKind.PROFIT_PER_SESSION;
  }
  if (goal === MetricKind.CLICKS || goal === MetricKind.CTR) {
    return MetricKind.CTR;
  }
  if (goal === MetricKind.ADD_TO_CART_RATE || goal === MetricKind.ADD_TO_CART) {
    return MetricKind.ADD_TO_CART_RATE;
  }
  if (
    goal === MetricKind.SUBSCRIPTIONS ||
    goal === MetricKind.SUBSCRIPTION_RATE
  ) {
    return MetricKind.SUBSCRIPTION_RATE;
  }
  if (goal === MetricKind.SIGNUPS || goal === MetricKind.SIGNUPS_RATE) {
    return MetricKind.SIGNUPS_RATE;
  }
  if (
    ![
      MetricKind.CONVERSION_RATE,
      MetricKind.AVG_ORDER_VALUE,
      MetricKind.TRANSACTIONS,
      MetricKind.PER_SESSION_VALUE,
      MetricKind.REVENUE,
    ].includes(goal)
  ) {
    return MetricKind.CONVERSION_RATE;
  }
  return MetricKind.CONVERSION_RATE;
};

function getExtraMetric(
  goal: MetricKind,
  atcUplift: number,
  clicksUplift: number,
  ppsUplift: number,
  signupsUplift: number,
  subsUplift: number,
  checkoutUplift: number,
): number {
  switch (goal) {
    case MetricKind.PROFIT_PER_SESSION:
    case MetricKind.PROFIT:
      return ppsUplift;
    case MetricKind.ADD_TO_CART:
    case MetricKind.ADD_TO_CART_RATE:
      return atcUplift;
    case MetricKind.CLICKS:
    case MetricKind.CTR:
      return clicksUplift;
    case MetricKind.SIGNUPS:
    case MetricKind.SIGNUPS_RATE:
      return signupsUplift;
    case MetricKind.SUBSCRIPTIONS:
    case MetricKind.SUBSCRIPTION_RATE:
      return subsUplift;
    case MetricKind.CHECKOUT_RATE:
      return checkoutUplift;
    default:
      return 0;
  }
}
